/**
 * mapping — the per-episode AssetMapping data model + pure helpers.
 *
 * An `AssetMapping` records which concrete `assetId` each visual symbol of a
 * scene resolves to. It is stored as JSON per episode and round-tripped through
 * the server (GET/PUT /mapping). The web side only ever deals in `assetId`s;
 * the server translates each `assetId` → an on-disk file path at compile time.
 *
 * These helpers are pure and immutable: every mutator returns a *new* mapping,
 * never editing the argument in place, so callers can drive React state safely.
 *
 * Symbol identity:
 *  - backgrounds / cg / music / sfx are keyed by `symbol` alone.
 *  - characters are keyed by `(charId, symbol)` — a look symbol like `smile`
 *    is only unique within its character.
 */

import type { ParsedSymbols } from "./symbols.js";

/** A background symbol bound to a concrete asset. */
export interface BackgroundBinding {
  readonly symbol: string;
  readonly assetId: string;
}

/** A single character look (pose) bound to a concrete asset. */
export interface LookBinding {
  readonly symbol: string;
  readonly assetId: string;
}

/** A character and its bound looks. */
export interface CharacterBinding {
  readonly id: string;
  readonly looks: LookBinding[];
}

/** A CG symbol bound to a concrete asset. */
export interface CgBinding {
  readonly symbol: string;
  readonly assetId: string;
}

/** A music track symbol bound to a concrete (uploaded) audio asset. */
export interface MusicBinding {
  readonly symbol: string;
  readonly assetId: string;
}

/** An SFX symbol bound to a concrete (uploaded) audio asset. */
export interface SfxBinding {
  readonly symbol: string;
  readonly assetId: string;
}

/**
 * The per-episode asset binding table (stored as JSON). The web side only ever
 * references `assetId`; the server resolves each to a file path at compile.
 *
 * `music` / `sfx` are audio families — audio can't be AI-generated, so these
 * symbols are bound only from uploaded files. Like backgrounds / cg they are
 * keyed by `symbol` alone.
 */
export interface AssetMapping {
  backgrounds: BackgroundBinding[];
  characters: CharacterBinding[];
  cg: CgBinding[];
  music: MusicBinding[];
  sfx: SfxBinding[];
}

/** The bindable asset families — three visual (bg/char/cg) + two audio (music/sfx). */
export type AssetKind = "background" | "character" | "cg" | "music" | "sfx";

/** Create an empty mapping (no bindings). */
export function emptyMapping(): AssetMapping {
  return { backgrounds: [], characters: [], cg: [], music: [], sfx: [] };
}

/**
 * Bind `symbol` to `assetId` within `kind`, returning a new mapping.
 *
 * Re-binding an already-bound symbol replaces its `assetId` in place (order is
 * preserved); a new symbol is appended. For `kind === "character"`, `charId`
 * is required — the look is keyed by `(charId, symbol)`; the character row is
 * created on first use. Passing `charId` for a non-character kind is ignored.
 *
 * Invalid input (unknown kind, missing charId for a character) returns the
 * mapping unchanged so callers never have to guard.
 */
export function bindSymbol(
  mapping: AssetMapping,
  kind: AssetKind,
  symbol: string,
  assetId: string,
  charId?: string,
): AssetMapping {
  switch (kind) {
    case "background":
      return { ...mapping, backgrounds: upsert(mapping.backgrounds, symbol, assetId) };
    case "cg":
      return { ...mapping, cg: upsert(mapping.cg, symbol, assetId) };
    case "music":
      return { ...mapping, music: upsert(mapping.music, symbol, assetId) };
    case "sfx":
      return { ...mapping, sfx: upsert(mapping.sfx, symbol, assetId) };
    case "character": {
      if (!charId) return mapping;
      return { ...mapping, characters: upsertLook(mapping.characters, charId, symbol, assetId) };
    }
    default:
      return mapping;
  }
}

/**
 * Remove the binding for `symbol` within `kind`, returning a new mapping.
 *
 * For `kind === "character"`, `charId` is required and only that character's
 * look is removed; if the character then has no looks left, its row is dropped.
 * Unbinding a symbol that isn't bound is a no-op (returns an equivalent mapping).
 */
export function unbindSymbol(
  mapping: AssetMapping,
  kind: AssetKind,
  symbol: string,
  charId?: string,
): AssetMapping {
  switch (kind) {
    case "background":
      return { ...mapping, backgrounds: mapping.backgrounds.filter((b) => b.symbol !== symbol) };
    case "cg":
      return { ...mapping, cg: mapping.cg.filter((b) => b.symbol !== symbol) };
    case "music":
      return { ...mapping, music: mapping.music.filter((b) => b.symbol !== symbol) };
    case "sfx":
      return { ...mapping, sfx: mapping.sfx.filter((b) => b.symbol !== symbol) };
    case "character": {
      if (!charId) return mapping;
      const characters = mapping.characters
        .map((c) =>
          c.id === charId ? { ...c, looks: c.looks.filter((l) => l.symbol !== symbol) } : c,
        )
        .filter((c) => c.looks.length > 0);
      return { ...mapping, characters };
    }
    default:
      return mapping;
  }
}

/** A symbol the parsed scene requires but the mapping has not bound. */
export interface MissingSymbol {
  readonly kind: AssetKind;
  readonly symbol: string;
  /** Present only for `kind === "character"`. */
  readonly charId?: string;
}

/** Coverage of a parsed scene's symbols against a mapping. */
export interface Coverage {
  /** Total visual symbols the scene references. */
  readonly total: number;
  /** How many of them are bound to an asset. */
  readonly bound: number;
  /** The unbound symbols, in scene order (backgrounds, characters, cg, music, sfx). */
  readonly missing: MissingSymbol[];
}

/**
 * Compute how many of a parsed scene's visual symbols are bound by `mapping`.
 *
 * A symbol counts as bound when the mapping has an entry with a non-empty
 * `assetId` for it. Character looks are matched by `(charId, symbol)`. Bindings
 * present in the mapping but absent from the scene don't affect the math —
 * coverage is always measured against what the scene actually requires.
 */
export function coverage(mapping: AssetMapping, parsed: ParsedSymbols): Coverage {
  const missing: MissingSymbol[] = [];
  let total = 0;
  let bound = 0;

  for (const symbol of parsed.backgrounds) {
    total++;
    if (isBound(mapping.backgrounds, symbol)) bound++;
    else missing.push({ kind: "background", symbol });
  }

  for (const character of parsed.characters) {
    const looks = mapping.characters.find((c) => c.id === character.id)?.looks ?? [];
    for (const symbol of character.looks) {
      total++;
      if (isBound(looks, symbol)) bound++;
      else missing.push({ kind: "character", symbol, charId: character.id });
    }
  }

  for (const symbol of parsed.cg) {
    total++;
    if (isBound(mapping.cg, symbol)) bound++;
    else missing.push({ kind: "cg", symbol });
  }

  for (const symbol of parsed.music) {
    total++;
    if (isBound(mapping.music, symbol)) bound++;
    else missing.push({ kind: "music", symbol });
  }

  for (const symbol of parsed.sfx) {
    total++;
    if (isBound(mapping.sfx, symbol)) bound++;
    else missing.push({ kind: "sfx", symbol });
  }

  return { total, bound, missing };
}

// ── internals ─────────────────────────────────────────────────────────────

interface Binding {
  readonly symbol: string;
  readonly assetId: string;
}

/** True when `symbol` has a binding with a non-empty assetId. */
function isBound(bindings: readonly Binding[], symbol: string): boolean {
  const b = bindings.find((x) => x.symbol === symbol);
  return b !== undefined && b.assetId !== "";
}

/** Insert or replace `symbol`'s assetId, preserving order. */
function upsert<T extends Binding>(bindings: T[], symbol: string, assetId: string): T[] {
  let replaced = false;
  const next = bindings.map((b) => {
    if (b.symbol === symbol) {
      replaced = true;
      return { ...b, assetId };
    }
    return b;
  });
  if (!replaced) next.push({ symbol, assetId } as T);
  return next;
}

/** Insert or replace a character look, creating the character row if needed. */
function upsertLook(
  characters: CharacterBinding[],
  charId: string,
  symbol: string,
  assetId: string,
): CharacterBinding[] {
  let found = false;
  const next = characters.map((c) => {
    if (c.id === charId) {
      found = true;
      return { ...c, looks: upsert(c.looks, symbol, assetId) };
    }
    return c;
  });
  if (!found) next.push({ id: charId, looks: [{ symbol, assetId }] });
  return next;
}
